<template>
  <div>
    <b-row>
      <b-col
        class="d-none d-lg-block"
        cols="2"
      >
        <product-filter
          :filter-display-loading="filterDisplayLoading"
          :filter-display="filterDisplay"
          :filter="searchFilter.filter"
          :constraints="searchFilter.constraints"
          @clear-filter="searchFilter.search = ''; searchFilter.filter = {}; searchFilter.constraints = { dateType: 'None', dateTo: '', dateFrom: '' }"
          @filter-change="updateFromFilter"
          @constraint-change="updateFromConstraint"
        />
      </b-col>

      <b-col
        cols="12"
        lg="10"
      >
        <div v-if="isLoggedIn">
          <recently-viewed-products compact />
        </div>

        <!-- ECommerce Header -->
        <section id="ecommerce-header">
          <div class="row">
            <div class="col-sm-12">
              <div class="ecommerce-header-items">
                <div class="d-flex justify-content-between align-items-center">
                  <h2 class="mb-1">
                    Full Structured Product Database
                  </h2>
                </div>
                <div class="result-toggler">
                  <b-button
                    variant="primary"
                    class="d-inline d-lg-none"
                    @click="filterSidebar = !filterSidebar"
                  >
                    <feather-icon
                      icon="FilterIcon"
                      class="mr-50"
                    />
                    <span class="align-middle">Filter</span>
                  </b-button>
                  <div class="search-results my-0 mb-1">
                    <h5>
                      {{ totalRows }} results
                      <!-- <span v-if="!productLoading && totalRows > perPage">| Showing results {{ `from ${showing[0]} to ${showing[1]}` }}</span> -->
                    </h5>
                  </div>
                </div>
                <div class="view-options d-flex">
                  <b-button
                    v-if="createPortfolioModal.newPortfolioId"
                    variant="primary"
                    @click="$router.push({name: 'portfolio-report', params: {portfolioType: createPortfolioModal.newPortfolioId}}); createPortfolioModal.newPortfolioId = null;"
                  >
                    View Portfolio
                  </b-button>
                  <template v-else>
                    <span id="analyse-results-wrapper" class="d-inline-block" tabindex="0">
                      <b-button
                        variant="primary"
                        :disabled="!isPremium || (productLoading || !totalRows) || (virtualPortfolioRunsLimit !== 0 && virtualPortfolioRunsUsage >= virtualPortfolioRunsLimit)"
                        @click="showPortfolioReport(false)"
                      >
                        Analyse Results
                        <feather-icon
                          v-if="!isPremium || virtualPortfolioRunsLimit !== 0 && virtualPortfolioRunsUsage >= virtualPortfolioRunsLimit"
                          icon="LockIcon"
                          class="ml-50"
                        />
                      </b-button>
                    </span>
                    <b-tooltip
                      v-if="!isPremium"
                      target="analyse-results-wrapper"
                      triggers="hover"
                      placement="top"
                    >
                      Upgrade to Premium to use this feature
                    </b-tooltip>
                    <b-tooltip
                      v-else-if="virtualPortfolioRunsLimit !== 0 && virtualPortfolioRunsUsage >= virtualPortfolioRunsLimit"
                      target="analyse-results-wrapper"
                      triggers="hover"
                      placement="top"
                    >
                      You have reached your daily limit of running {{ virtualPortfolioRunsLimit }} virtual portfolios
                    </b-tooltip>

                  </template>
                  <span id="add-to-portfolio-wrapper" class="d-inline-block" tabindex="0">
                    <b-button
                      variant="primary"
                      class="ml-1"
                      :disabled="!isPremium || productLoading || !totalRows"
                      @click="addProductsToPortfolio.show = true"
                    >
                      Add To Portfolio
                      <feather-icon
                        v-if="!isPremium"
                        icon="LockIcon"
                        class="ml-50"
                      />
                    </b-button>
                  </span>
                  <b-tooltip
                    v-if="!isPremium"
                    target="add-to-portfolio-wrapper"
                    triggers="hover"
                    placement="top"
                  >
                    Upgrade to Premium to use this feature
                  </b-tooltip>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Searchbar -->
        <div class="ecommerce-searchbar mt-1 mb-2">
          <b-input-group class="input-group-merge">
            <b-form-input
              v-model="searchFilter.search"
              placeholder="Search (ID, name or identifier)"
              class="search-product"
              @change="doSearch(); updateFilters();"
            />
            <b-input-group-append is-text>
              <feather-icon
                icon="SearchIcon"
                class="text-muted"
              />
            </b-input-group-append>
          </b-input-group>
        </div>

        <!-- Pagination Result Label for Mobile -->
        <div class="d-block d-lg-none mb-1 font-weight-bold">
          {{ totalRows }} results found <span v-if="!productLoading && totalRows > perPage">| Showing results {{ `from ${showing[0]} to ${showing[1]}` }}</span>
        </div>

        <b-card
          id="product-table"
          no-body
        >
          <b-table
            v-if="!productError"
            responsive
            hover
            :items="items"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            :no-local-sorting="true"
            @row-clicked="productClicked"
            @row-middle-clicked="productClicked"
          >
            <template #cell(toggle)="data">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-primary"
                class="btn-icon rounded-circle"
                @click.prevent="data.toggleDetails"
              >
                <feather-icon :icon="data.detailsShowing ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
              </b-button>
            </template>

            <template #cell(producttype)="data">
              <div class="d-flex align-items-center">
                <b-avatar
                  class="mr-1"
                  :variant="data.item.pticoncolor"
                >
                  <feather-icon
                    size="18"
                    :icon="data.item.pticonname"
                  />
                </b-avatar>
                <span>{{ data.item.producttypename }}</span>
              </div>
            </template>

            <template #cell(productname)="data">
              <div class="d-flex align-items-center">
                <b-img
                  v-if="data.item.providerid"
                  :src="`${baseURL}serveproviderlogo.cfm?id=${data.item.providerid}`"
                  alt="provider logo"
                  class="mr-1"
                  style="width: 32px; height: 32px; object-fit: contain;"
                />
                <span>{{ data.item.productname }}</span>
              </div>
            </template>

            <template #cell(numportfolios)="data">
              <b-avatar
                variant="light-success"
              >
                <b-button
                  v-if="isPremium"
                  variant="outline-success"
                  @click.prevent="addProductToPortfolio.product=data.item; addProductToPortfolio.show = true;"
                >
                  <template v-if="data.item.numportfolios > 0">
                    {{ data.item.numportfolios.toString() }}
                  </template>
                  <feather-icon
                    v-else
                    size="18"
                    icon="PlusIcon"
                  />
                </b-button>
                <b-button
                  v-else
                  :disabled="true"
                  variant="outline-success"
                >
                  <feather-icon
                    icon="LockIcon"
                    size="18" />
                </b-button>
              </b-avatar>
            </template>

            <template #row-details="row">
              <b-card class="m-0 p-0">
                <b-row>
                  <b-col lg="6">
                    <b-table
                      responsive
                      :hover="false"
                      :thead-class="'d-none'"
                      :fields="dropdownGeneralFields"
                      :items="row.item.dropdownterm === '' ? [] : JSON.parse(row.item.dropdownterm).productterms"
                    />
                  </b-col>
                  <b-col lg="6">
                    <b-table
                      responsive
                      :thead-class="'d-none'"
                      :fields="dropdownGeneralFields"
                      :items="row.item.dropdownterm === '' ? [] : JSON.parse(row.item.dropdownterm).coverinfo"
                    />
                  </b-col>
                </b-row>
              </b-card>
              <b-card class="m-0 p-0">
                <b-table
                  responsive
                  :hover="false"
                  :fields="dropdownUnderlyingFields"
                  :items="row.item.dropdownterm === '' ? [] : JSON.parse(row.item.dropdownunderlying).content"
                />
              </b-card>
            </template>
            <template #cell(date_1_strike)="data">
              <div class="d-flex flex-column gap-0.5">
                <span class="text-center">{{ formatIsoDate(data.item.date_1_strike) }}</span>
              </div>
            </template>
            <template #cell(date_2_finalmarket)="data">
              <div class="d-flex flex-column gap-0.5">
                <span class="text-center">{{ formatIsoDate(data.item.date_2_finalmarket) }}</span>
              </div>
            </template>
            <template #cell(terminationdate)="data">
              <div class="d-flex flex-column gap-0.5">
                <span class="text-center">{{ data.item.terminationdate ? formatIsoDate(data.item.terminationdate) : '' }}</span>
              </div>
            </template>
          </b-table>
          <error-display
            v-else
            @action-clicked="doSearch"
          />

          <div
            v-if="productLoading"
            class="w-100 text-center py-5 d-flex flex-column justify-content-center align-items-center"
            :style="`height: ${loadingContainerHeight}`"
          >
            <b-spinner variant="primary" />
          </div>
          <no-data-container
            v-else-if="items.length < 1 && !productError"
            message="No Products Matched Your Search"
          />

          <div class="d-flex justify-content-between px-2">
            <div class="my-2">
              <span class="pb-1">Rows Per Page</span>
              <b-form-select
                v-model="perPage"
                :options="perPageOptions"
                class="pagination-select"
              />
            </div>

            <div class="my-2 d-flex flex-column justify-content-end">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                align="center"
                size="lg"
                class="mb-0"
              />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-sidebar
      v-model="filterSidebar"
      :backdrop="true"
      :no-header="true"
      :body-class="filterSidebarClass"
    >
      <div class="p-2">
        <product-filter
          :filter-display="filterDisplay"
          :filter="filterSelections"
          :constraints="searchFilter.constraints"
          @clear-filter="searchFilter.search = ''; filterSelections = {};"
          @filter-change="updateFromFilter"
        />
      </div>
    </b-sidebar>

    <!-- Create Portfolio Modal -->
    <b-modal
      v-if="maxNumberOfProductsInPortfolio === 0 || totalRows <= maxNumberOfProductsInPortfolio"
      v-model="createPortfolioModal.show"
      centered
      :title="`Create Portfolio Using ${totalRows} Products`"
      hide-footer
    >
      <create-portfolio-modal-content
        :portfolios="portfolios"
        :search-filter="searchFilter"
        :view-portfolio="createPortfolioModal.viewAfterCreation"
        description="product"
        @portfolio-created="handlePortfolioCreated"
      />
    </b-modal>
    <b-modal
      v-else-if="createPortfolioModal.virtual"
      v-model="createPortfolioModal.show"
      ok-title="Proceed with Limited Product Set"
      cancel-variant="danger"
      cancel-title="Cancel"
      modal-class="modal-danger"
      centered
      title="Product Per Portfolio Limit Reached"
      @ok="showPortfolioReport(true); createPortfolioModal.virtual = false"
      @hide="createPortfolioModal.virtual = false"
    >
      <b-card-text>
        You can analyse a maximum of {{ filterSelectionMax }} products in one portfolio.
        The analysis will include only the first {{ filterSelectionMax }} products from your search results.
      </b-card-text>
    </b-modal>
    <b-modal
      v-else
      v-model="createPortfolioModal.show"
      ok-only
      ok-variant="danger"
      ok-title="Close"
      modal-class="modal-danger"
      centered
      title="Product Per Portfolio Limit Reached"
    >
      <b-card-text>
        You can only include {{ maxNumberOfProductsInPortfolio }} products in one portfolio. Try to narrow your search
        to get under the limit.
      </b-card-text>
    </b-modal>

    <!-- Add Product to Portfolio Modal -->
    <b-modal
      v-model="addProductToPortfolio.show"
      centered
      title="Add Product To Portfolio"
      hide-footer
    >
      <add-product-to-portfolio-modal-content
        :product="addProductToPortfolio.product"
        @portfolioUpdated="onProductAddedToPortfolio"
      />
    </b-modal>

    <!-- Add Product<b>s</b> to Portfolio Modal -->
    <b-modal
      v-if="maxNumberOfProductsInPortfolio === 0 || totalRows <= maxNumberOfProductsInPortfolio"
      v-model="addProductsToPortfolio.show"
      centered
      title="Add Products To Portfolio"
      hide-footer
    >
      <add-products-to-portfolio-modal-content
        :portfolios="portfolios"
        :search-filter="searchFilter"
        @products-added="onProductsAddedToPortfolio"
        @create-portfolio="onCreatePortfolio"
      />
    </b-modal>
    <b-modal
      v-else
      v-model="addProductsToPortfolio.show"
      ok-only
      ok-variant="danger"
      ok-title="Close"
      modal-class="modal-danger"
      centered
      title="Product Per Portfolio Limit Reached"
    >
      <b-card-text>
        You can only include {{ maxNumberOfProductsInPortfolio }} products in a portfolio. Try to narrow your search
        to get under the limit.
      </b-card-text>
    </b-modal>

  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BInputGroup,
  BInputGroupAppend,
  BFormInput,
  BCard,
  BCardText,
  BTooltip,
  BTable,
  BAvatar,
  BButton,
  BPagination,
  BFormSelect,
  BSpinner,
  BModal,
  BSidebar,
  BImg,
} from 'bootstrap-vue'
import strings from '@/strings'
import CreatePortfolioModalContent from '@/views/components/CreatePortfolioModalContent.vue'
import NoDataContainer from '@/views/components/NoDataContainer.vue'
import AddProductsToPortfolioModalContent from '@/views/components/AddProductsToPortfolioModalContent.vue'
import AddProductToPortfolioModalContent from '@/views/components/AddProductToPortfolioModalContent.vue'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import ProductFilter from '@/views/components/ProductFilter.vue'
import ErrorDisplay from '@/views/components/ErrorDisplay.vue'
import { formatIsoDate } from '@/utils/formatters'
import { mapGetters } from 'vuex'
import RecentlyViewedProducts from './current/RecentlyViewedProducts.vue'

export default {
  name: 'Products',
  components: {
    AddProductToPortfolioModalContent,
    AddProductsToPortfolioModalContent,
    NoDataContainer,
    BRow,
    BCol,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
    BCard,
    BCardText,
    BTooltip,
    BTable,
    BAvatar,
    BButton,
    BPagination,
    BFormSelect,
    BSpinner,
    BModal,
    BSidebar,
    BImg,
    ProductFilter,
    CreatePortfolioModalContent,
    ErrorDisplay,
    RecentlyViewedProducts,
  },
  directives: {
    Ripple,
  },
  data() {
    // Total Width: 1300
    // ID:
    return {
      baseURL: process.env.VUE_APP_BACKEND_PATH,
      filterSidebar: false,
      filterDisplayLoading: true,
      filterSelections: {},
      clickedProductId: '',
      fields: [
        // Dropdown hidden
        // { key: 'toggle', label: 'Show Details', thStyle: { width: '5%' } },
        {
          key: 'productid', label: 'Id', thStyle: { width: '11%' }, sortable: true,
        },
        {
          key: 'producttype', label: 'Type', thStyle: { width: '16%' }, sortable: true,
        },
        {
          key: 'productname', label: 'Name', thStyle: { width: '40' }, sortable: true,
        },
        {
          key: 'date_1_strike', label: 'Strike Date', thStyle: { width: '13%' }, sortable: true,
        },
        {
          key: 'date_2_finalmarket', label: 'Final Date', thStyle: { width: '13%' }, sortable: true,
        },
        {
          key: 'terminationdate', label: 'Termination Date', thStyle: { width: '13%' }, sortable: true,
        },
        {
          key: 'numportfolios', label: 'Port-folios', thStyle: { width: '5%' },
        },
      ],
      items: [],
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filterDisplay: [],
      searchFilter: {
        search: '',
        filter: {},
        constraints: {
          dateFrom: '',
          dateTo: '',
          dateType: 'None',
        },
      },
      dropdownGeneralFields: ['field', 'value'],
      dropdownUnderlyingFields: [{
        label: 'Underlying Name',
        key: 'underlying',
      }, {
        label: '% of Initial Value',
        key: 'relvalue',
      }, {
        label: 'Status',
        key: 'status',
      }],
      currentPage: 1,
      perPage: 20,
      previousPage: null,
      nextPage: null,
      totalPages: 0,
      totalRows: 0,
      perPageOptions: [5, 10, 20, 50, 100, 500],
      productLoading: true,
      loadingContainerHeight: '200px',
      allProductIds: [],
      createPortfolioModal: {
        show: false,
        virtual: false,
        newPortfolioId: null,
        viewAfterCreation: false,
      },
      addProductToPortfolio: {
        show: false,
        loading: false,
        saveLoading: false,
        product: null,
        portfolios: [],
        in: [],
        out: [],
      },
      addProductsToPortfolio: {
        show: false,
      },
      productError: null,
      cancelToken: undefined,
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
      'isPremium',
      'portfolioMaxProducts',
      'filterSelectionMax',
      'virtualPortfolioRunsLimit',
    ]),
    ...mapGetters('app', [
      'virtualPortfolioRunsUsage',
    ]),
    showing() {
      const start = this.previousPage ? (this.currentPage * this.perPage) - this.perPage + 1 : 1
      const end = (start === 1 ? 0 : start - 1) + this.items.length
      return [start, end]
    },
    portfolioIn() {
      return this.addProductToPortfolio.portfolios.filter(portfolio => portfolio.in !== false)
    },
    portfolioNotIn() {
      return this.addProductToPortfolio.portfolios.filter(portfolio => portfolio.in === false)
    },
    maxNumberOfProductsInPortfolio() {
      return this.portfolioMaxProducts
    },
    portfolios() {
      return this.$store.state.app.portfolios
    },
    filterSidebarClass() {
      return this.$store.state.appConfig.layout.skin === 'dark' ? 'filter-sidebar-dark-bg custom-scrollbar' : 'filter-sidebar-light-bg custom-scrollbar'
    },
  },
  watch: {
    currentPage() {
      this.doSearch()
    },
    perPage() {
      if (this.currentPage === 1) {
        this.doSearch()
      } else {
        this.currentPage = 1
      }
      window.scrollTo(0, 0)
    },
    sortBy() {
      this.doSearch()
    },
    sortDesc() {
      this.doSearch()
    },
  },
  async created() {
    if (this.$route.params.category) {
      this.filterSelections = { [this.$route.params.category]: [String(this.$route.params.value)] }
      await this.loadFilters(false)
      this.updateFromFilter(this.filterSelections)
    } else {
      this.loadFilters()
      this.doSearch()
    }
  },
  activated() {
    this.$nextTick(() => {
      this.$root.$emit('bv::refresh::tooltip')
    })

    if (this.$route.params.category) {
      this.filterSelections = { [this.$route.params.category]: [String(this.$route.params.value)] }
      this.updateFromFilter(this.filterSelections)
    }
  },
  deactivated() {
    this.createPortfolioModal.newPortfolioId = null
  },
  methods: {
    formatIsoDate,
    productClicked(item, index, event) {
      this.clickedProductId = item.productid

      if (event.ctrlKey || event.button === 1) {
        const route = this.$router.resolve({ name: 'product-report', params: { reportNumber: this.clickedProductId } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: 'product-report', params: { reportNumber: this.clickedProductId } })
      }
    },
    handleContext(item, index, event) {
      event.preventDefault()
      this.$refs.openInTabContext.open(event, item)
    },
    openInNewTab(item) {
      const route = this.$router.resolve({ name: 'product-report', params: { productId: item.productid } })
      window.open(route.href, '_blank')
    },
    showPortfolioReport(reduce = false) {
      if (this.filterSelectionMax > 0 && (this.totalRows > this.filterSelectionMax) && !reduce) {
        this.createPortfolioModal.virtual = true
        this.createPortfolioModal.show = true
        return
      }

      // Include limit and sort params inside searchFilter for virtual portfolio
      const virtualSearchFilter = {
        ...this.searchFilter,
        reduceToLimit: reduce,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
      }

      this.$router.push({
        name: 'portfolio-report',
        params: {
          portfolioType: 'v',
          searchFilter: virtualSearchFilter,
        },
      })
    },
    updateFromFilter(newFilter) {
      this.searchFilter.filter = newFilter
      this.updateFilters()
      this.doSearch()
    },
    updateFromConstraint(newConstraints) {
      this.searchFilter.constraints = newConstraints
      this.updateFilters()
      this.doSearch()
    },
    doSearch(showLoading = true) {
      // Abort previous requests
      // Check if there are any previous pending requests
      if (typeof this.cancelToken !== typeof undefined) {
        this.cancelToken.cancel('Operation canceled due to new request.')
        this.productLoading = true
      }

      this.cancelToken = axios.CancelToken.source()

      // Get height of product table card
      const productTable = document.getElementById('product-table')
      const height = productTable ? productTable.offsetHeight : 0

      if (showLoading) {
        this.productLoading = true
        this.loadingContainerHeight = height ? `${(height - 177)}px` : '200px'
        this.items = []

        // Remove newPortfolioId value to show 'Analyse Result' button instead of 'View Portfolio' button
        this.createPortfolioModal.newPortfolioId = null
      }

      // Reset error flag
      this.productError = null

      this.$http
        .get('lifecycle/public/data.cfc?method=getFilterResults', {
          params: {
            searchFilter: { ...this.searchFilter, sortBy: this.sortBy, sortDesc: this.sortDesc },
            perpage: this.perPage,
            page: this.currentPage,
          },
          cancelToken: this.cancelToken.token,
        })
        .then(response => {
          this.items = response.data.DATA
          this.previousPage = response.data.INFO.PREVIOUSPAGE
          this.nextPage = response.data.INFO.NEXTPAGE
          this.currentPage = response.data.INFO.CURRENTPAGE * 1
          this.totalPages = response.data.INFO.NUMPAGES
          this.perPage = response.data.INFO.PERPAGE
          this.totalRows = response.data.INFO.NUMROWS
          this.allProductIds = response.data.INFO.ALLPRODUCTIDS

          this.cancelToken = undefined
          this.productLoading = false
        })
        .catch(error => {
          if (error.response || error.request) {
            this.productError = true
            this.productLoading = false
          }
        })
    },
    async loadFilters(toggleLoading = true) {
      this.filterDisplayLoading = true
      return this.$http
        .get('productfilter.cfc?method=getFilterDisplay', {
          params: {
            searchFilter: this.searchFilter,
          },
        })
        .then(response => {
          this.filterDisplay = response.data.content

          // Index the filter data so that items can be looked up by value
          this.filterDisplay.forEach(category => {
            // eslint-disable-next-line no-param-reassign
            category.valueIndex = {}
            category.items.forEach((item, index) => {
              // eslint-disable-next-line no-param-reassign
              category.valueIndex[item.value] = index
            })
          })
          if (toggleLoading) {
            this.filterDisplayLoading = false
          }
        })
    },
    updateFilters() {
      // NOTE add this in ?/
      // this.filterDisplayLoading = true
      this.$http
        .get('productfilter.cfc?method=getFilterDisplay', {
          params: {
            searchFilter: this.searchFilter,
          },
        })
        .then(response => {
          // Build a lookup table of non-slider response categories by categoryKey
          const responseMap = {}
          response.data.content.forEach(cat => {
            if (!cat.slider) {
              responseMap[cat.categoryKey] = cat
            }
          })

          // Merge into local filterDisplay
          this.filterDisplay.forEach(displayCategory => {
            // Skip sliders entirely - they should only update on search, not filter changes
            if (displayCategory.slider) return

            const key = displayCategory.categoryKey
            const responseCategory = responseMap[key]

            // --- Reset all counts to 0 ---
            displayCategory.items.forEach(item => {
              const updatedItem = item
              updatedItem.count = 0
            })

            // --- Merge updated counts, if response category exists ---
            if (responseCategory) {
              const updatedDisplayCategory = displayCategory
              responseCategory.items.forEach(respItem => {
                const idx = updatedDisplayCategory.valueIndex[respItem.value]
                if (idx !== undefined) {
                  updatedDisplayCategory.items[idx].count = respItem.count
                }
              })
            }

            this.filterDisplayLoading = false
          })
        })
    },
    updateFiltersWithSliders() {
      this.$http
        .get('productfilter.cfc?method=getFilterDisplay', {
          params: {
            searchFilter: this.searchFilter,
          },
        })
        .then(response => {
          // Build lookup tables for both slider and non-slider categories
          const responseMap = {}
          const sliderResponseMap = {}
          response.data.content.forEach(cat => {
            if (cat.slider) {
              sliderResponseMap[cat.categoryKey] = cat
            } else {
              responseMap[cat.categoryKey] = cat
            }
          })

          // Merge into local filterDisplay
          this.filterDisplay.forEach(displayCategory => {
            const key = displayCategory.categoryKey

            // Handle slider categories
            if (displayCategory.slider) {
              const responseSlider = sliderResponseMap[key]
              if (responseSlider && responseSlider.slider) {
                const updatedDisplayCategory = displayCategory
                // Update slider items (data points)
                updatedDisplayCategory.slider.items = responseSlider.slider.items
                // Update custom labels if they exist
                if (responseSlider.slider.customLabels) {
                  updatedDisplayCategory.slider.customLabels = responseSlider.slider.customLabels
                }
              }
              return
            }

            // Handle non-slider categories
            const responseCategory = responseMap[key]

            // --- Reset all counts to 0 ---
            displayCategory.items.forEach(item => {
              const updatedItem = item
              updatedItem.count = 0
            })

            // --- Merge updated counts, if response category exists ---
            if (responseCategory) {
              const updatedDisplayCategory = displayCategory
              responseCategory.items.forEach(respItem => {
                const idx = updatedDisplayCategory.valueIndex[respItem.value]
                if (idx !== undefined) {
                  updatedDisplayCategory.items[idx].count = respItem.count
                }
              })
            }
          })
        })
    },
    createPortfolio() {
      this.createPortfolioModal.loading = true
      this.createPortfolioModal.validationError = null

      // Check if the portfolio name is already used
      const nameExists = this.portfolios.find(portfolio => portfolio.portfolioName === this.createPortfolioModal.portfolioName)

      if (nameExists) {
        this.createPortfolioModal.loading = false
        // Set input error message
        this.createPortfolioModal.validationError = 'Portfolio Name Already Exists. Try Another One.'
        return
      }

      this.$http.post('lifecycle/public/portfolio.cfc?method=createPortfolio', new URLSearchParams({
        portfolioName: this.createPortfolioModal.portfolioName,
        productIds: this.allProductIds,
      })).then(() => {
        this.$bvToast.toast('Portfolio Created', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
        this.createPortfolioModal.portfolioName = ''
        this.createPortfolioModal.show = false

        // Update store value
        this.$store.dispatch('app/updatePortfolios')
        this.$store.commit('app/SET_PORTFOLIO_UPDATED', true)
      }).catch(error => {
        console.log('Error creating portfolio: ', error)
        this.$bvToast.toast(strings.defaultErrorMessage, {
          title: strings.defaultErrorTitle,
          variant: 'danger',
          solid: true,
        })
      }).then(() => {
        this.createPortfolioModal.loading = false
      })
    },
    loadPortfoliosByProduct(id) {
      this.addProductToPortfolio.loading = true

      this.$http.get(`lifecycle/public/portfolio.cfc?method=getPortfoliosByProduct&productId=${id}`)
        .then(response => {
          // Sort by portfolios `in` property
          const productPortfolios = response.data.portfolios
          productPortfolios.sort((a, b) => {
            if (a.in && !b.in) {
              return -1
            }
            return 1
          })
          this.addProductToPortfolio.portfolios = productPortfolios
        }).catch(() => {
          this.$bvToast.toast('Fetching Portfolios Failed', {
            title: strings.defaultErrorTitle,
            variant: 'danger',
            solid: true,
          })
        }).then(() => {
          this.addProductToPortfolio.loading = false
        })
    },
    updateProductPortfolio() {
      this.addProductToPortfolio.saveLoading = true

      this.$http.post('portfolio.cfc?method=replacePortfoliosByProduct', new URLSearchParams({
        productId: this.addProductToPortfolio.product.productid,
        portfolioIds: this.portfolioIn.map(p => p.portfolioId),
      }))
        .then(() => {
          this.$bvToast.toast('Product Portfolio Updated', {
            title: 'Success',
            variant: 'success',
            solid: true,
          })
          this.addProductToPortfolio.show = false

          // Reload product table data to show correct number of portfolios on the table
          this.doSearch(false)

          // Update store value
          this.$store.dispatch('app/updatePortfolios')
          this.$store.commit('app/SET_PORTFOLIO_UPDATED', true)
        })
        .catch(() => {
          this.$bvToast.toast(strings.defaultErrorMessage, {
            title: strings.defaultErrorTitle,
            variant: 'danger',
            solid: true,
          })
        })
        .then(() => {
          this.addProductToPortfolio.saveLoading = false
        })
    },
    handlePortfolioCreated(portfolio) {
      this.createPortfolioModal.show = false
      // this.createPortfolioModal.newPortfolioId = portfolioId

      this.$bvToast.toast('Portfolio Created', {
        title: 'Success',
        variant: 'success',
        solid: true,
      })

      // Reload product table data to show correct number of portfolios on the table
      this.doSearch(false)

      // Update portfolios list inside the store
      this.$store.dispatch('app/updatePortfolios')
      this.$store.commit('app/SET_PORTFOLIO_UPDATED', true)
      this.$store.commit('app/ADD_UPDATED_PORTFOLIO', portfolio.portfolioId)

      // Navigate to portfolio report page
      if (portfolio.viewPortfolio) {
        this.$router.push({ name: 'portfolio-report', params: { portfolioType: portfolio.portfolioId } })
      }
    },
    onProductsAddedToPortfolio(portfolio) {
      this.addProductsToPortfolio.show = false
      this.$bvToast.toast('Products Added to Portfolio', {
        title: 'Success',
        variant: 'success',
        solid: true,
      })

      // Reload product table data to show correct number of portfolios on the table
      this.doSearch(false)

      // Update portfolios list inside the store
      this.$store.dispatch('app/updatePortfolios')
      this.$store.commit('app/ADD_UPDATED_PORTFOLIO', portfolio.portfolioId)

      // Navigate to portfolio report page
      if (portfolio.viewPortfolio) {
        this.$router.push({ name: 'portfolio-report', params: { portfolioType: portfolio.portfolioId } })
      }

      // Set portfolio ID for 'View Portfolio' button. This will change the 'Analyse Results' button to 'View Portfolio'
      // this.createPortfolioModal.newPortfolioId = portfolioId
    },
    onProductAddedToPortfolio(portfolioIds) {
      this.addProductToPortfolio.show = false

      this.$bvToast.toast('Product Portfolio Updated', {
        title: 'Success',
        variant: 'success',
        solid: true,
      })

      // Reload product table data to show correct number of portfolios on the table
      this.doSearch(false)

      // Update store value
      this.$store.dispatch('app/updatePortfolios')
      this.$store.commit('app/SET_PORTFOLIO_UPDATED', true)
      this.$store.commit('app/ADD_UPDATED_PORTFOLIOS', portfolioIds)
    },
    onCreatePortfolio(viewAfterCreation) {
      this.addProductsToPortfolio.show = false
      this.createPortfolioModal.show = true
      this.createPortfolioModal.viewAfterCreation = Boolean(viewAfterCreation)
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/pages/app-ecommerce.scss";

.pagination-select {
  max-width: 200px;
}
</style>
